/**
 * Add a function to date to determine Daylight savings
 * Check for DST by getting a day in january and july then compares the offset.
 */
Date.prototype.isDst = function () {
  const currentOffset = this.getTimezoneOffset();
  const jan = new Date(this.getFullYear(), 0, 1);
  const jul = new Date(this.getFullYear(), 6, 1);
  const dstOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
  return currentOffset < dstOffset;
};

/**
 * Petsense CRM API
 * Go through all the api request through this class to interact with the Petsense API
 * All the API calls return promises that can be resolved by the callers.
 */
class PetsenseCrmApi {
  constructor(petsenseApiUrl) {
    this.baseUrl = petsenseApiUrl;
    this.cdpFlag = JSON.parse(
      document.querySelector("[data-cdp-api-flag]")?.innerHTML
    );
  }

  parseParams(params) {
    return Object.keys(params)
      .map((key) => key + "=" + params[key])
      .join("&");
  }

  // Get JWT Token responsable for auth into the API
  getToken() {
    return fetch(`${this.baseUrl}/getAccessTokenShopifyPtsGrooming`, {
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => data.jwt);
  }

  // Get Profile list of the currently logged in Shopify user from TSC CRM, which contains all pet profiles ids attached to it
  getPetProfileList(token, params = {}) {
    return fetch(
      `${this.baseUrl}/PetProfileListShopifyGrooming?${this.parseParams({
        ...params,
      })}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        return data.PetProfiles;
      });
  }

  // Get individual profiles per pet
  getPetProfileDetails(token, params = {}) {
    return fetch(
      `${this.baseUrl}/GetPetProfileInfoShopifyGrooming?${this.parseParams({
        ...params,
      })}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => data.PetProfile);
  }

  // Add Pet Profile, and return the newly created id of the pet
  addPetProfile(token, customerId, profile = {}) {
    // Their API requires this envelope object that list type of actions being made, the timestamp is mapped to current time on CST
    const envelope = {
      Target: "CRM",
      Action: "ADD",
      Version: "1.0",
      DocType: "PetProfile",
      TimeStamp: this.generateCSTTimeStamp(),
      Source: "Shopify",
      ShopifyCustomerId: customerId,
    };

    return fetch(`${this.baseUrl}/PetProfileShopifyGrooming`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ Envelope: envelope, Profile: profile }),
    })
      .then((response) => response.json())
      .then((data) => {
        return data.PetProfile[0].ProfileId;
      });
  }

  // Edit Pet Profile
  editPetProfile(token, customerId, profile = {}) {
    // Their API requires this envelope object that list type of actions being made, the timestamp is mapped to current time on
    const envelope = {
      Target: "CRM",
      Action: "EDIT",
      Version: "1.0",
      DocType: "PetProfile",
      TimeStamp: this.generateCSTTimeStamp(),
      Source: "Shopify",
      ShopifyCustomerId: customerId,
    };

    return fetch(`${this.baseUrl}/PetProfileShopifyGrooming`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ Envelope: envelope, Profile: profile }),
    })
      .then((response) => response.json())
      .then((data) => data);
  }

  // Get all pet breeds, but filter specifically for dog
  getPetBreeds(token) {
    return (
      fetch(`${this.baseUrl}/PetProfileLookupShopifyGrooming`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => response.json())
        // Because this api returns all the different animal breeds of different types, we filter to only want dog breeds.
        .then(
          (data) =>
            data.PetLookupServices.AnimalTypeBreeds.filter(
              (type) => type.AnimalType === "Dog"
            )[0].AnimalBreeds
        )
    );
  }

  // Get the customer from crm based on shopifyId, return the first result as we only pass 1 id
  getCustomer(token, customerId) {
    return fetch(
      `${this.baseUrl}/GetCustProfileShopifyGrooming?shopifyCustomerId=${customerId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        return data?.results?.[0];
      })
      .catch((error) => {
        console.error("Error:", error);
        return error;
      });
  }

  // Create customer and get the partnerId for sending later to waitwhile
  createCustomer(token, customer) {
    if (this.cdpFlag?.enabled) {
      customer.operation = "create";
    }

    return fetch(`${this.baseUrl}/CreateCustProfileShopifyGrooming`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(customer),
    })
      .then((response) => response.json())
      .then((data) => {
        return data.partnerId;
      });
  }

  // Generate a current time in CST or CDT with the following format yyyy-mm-ddThh:mm:ssZ
  generateCSTTimeStamp() {
    // Minutes and hours in milliseconds
    const minutes = 60000;
    const hours = 3600000;
    const currentTime = new Date();
    // Offset depends if there is daylight time currently on this date.
    const offset = currentTime.isDst() ? 5 : 6;
    // Get utc time without an offset
    let utc = currentTime.getTime() + currentTime.getTimezoneOffset() * minutes;
    let newTime = new Date(utc - hours * offset);
    const month = String(newTime.getMonth() + 1).padStart(2, "0");
    const date = String(newTime.getDate()).padStart(2, "0");
    return `${newTime.getFullYear()}-${month}-${date}T${newTime.getHours()}:${newTime.getMinutes()}:${newTime.getSeconds()}Z`;
  }
}

function rot47(str) {
  let encoded = "";
  for (let i = 0; i < str.length; i++) {
    let charNum = str.charCodeAt(i);
    if (charNum >= 33 && charNum <= 126) {
      encoded += String.fromCharCode(33 + ((charNum + 14) % 94));
    } else {
      encoded += String.fromCharCode(charNum);
    }
  }
  return encoded;
}

// Due the way the API returns their date we need to reformat before prefilling the dates in the form
// Convert from YYYYMMDD to YYYY-MM-DD
function convertTSCDateToFormDate(date) {
  return date
    ? date.slice(0, 4) + "-" + date.slice(4, 6) + "-" + date.slice(6)
    : "";
}

// Due the way the API accepts dates we need to reformat before sending the dates
// Convert from YYYY-MM-DD to MM-DD-YYYY
function convertFormDateToTSCDate(date) {
  const dateArray = date.split("-");
  return date ? dateArray[1] + "-" + dateArray[2] + "-" + dateArray[0] : "";
}

function clearForm(elements) {
  for (let i = 0; i < elements.length; i++) {
    const input = elements[i];
    switch (input.type) {
      case "text":
      case "date":
        input.value = "";
        break;
      case "checkbox":
        input.checked = false;
        break;
      case "select-one":
        // We do not want to shuffle the index selected Pet when clearing the form, as this will trigger an infinite loop
        if (input.name !== "pets") {
          input.selectedIndex = 0;
        }
        break;
    }
  }
}

function mapFormToProfile(elements) {
  return {
    ProfileId: elements["pets"].value,
    AnimalType: elements["type"].value,
    Breeds: [
      {
        BreedName: elements["breed"].value,
        BreedId: elements["breed"].value,
      },
    ],
    AdoptionDate: convertFormDateToTSCDate(elements["adoptionDate"].value),
    BirthDate: convertFormDateToTSCDate(elements["birthDate"].value),
    Weight: {
      Range: elements["weight"].value,
    },
    Name: elements["petName"].value,
    SpayedorNeutered: elements["spray_neuter"].checked ? "Y" : "N",
  };
}

function mapProfileToForm(profile, elements) {
  elements["petName"].value = profile.Name;
  elements["weight"].value = profile.Weight.Range;
  elements["birthDate"].value = convertTSCDateToFormDate(profile.BirthDate);
  elements["adoptionDate"].value = convertTSCDateToFormDate(
    profile.AdoptionDate
  );
  elements["spray_neuter"].checked = profile.SpayedorNeutered === "Y";

  // Only get the first breed even if there are multiple.
  const breed = profile.Breeds[0]?.BreedName;
  // Only prefill the value if the is a valid option.
  const breedSelect = elements["breed"];
  for (let i = 0; i < breedSelect.options.length; i++) {
    if (breedSelect.options[i].value === breed) {
      breedSelect.value = breedSelect.options[i].value;
      break;
    }
  }
}

function mapShopifyCustomerToCrmCustomer(elements) {
  return {
    firstName: elements["first_name"].value,
    lastName: elements["last_name"].value,
    homePhone: elements["phone"].value,
    homePhoneIndicator: "P01",
    email: elements["email"].value,
    emailIndicator: "E01",
    shopifyCustomerId: elements["id"].value,
  };
}

// Simple mapping instead of a .entries of FormData as not all fields are needed, the id of datafields will change in PROD.
function mapWaitwhileJSON(elements, crmId) {
  const waitwhileJSON = {
    firstname: elements["first_name"].value,
    lastname: elements["last_name"].value,
    email: elements["email"].value,
    phone: elements["phone"].value,
    externalcustomerid: crmId,
  };

  //These fields have different id depending on which store we are on.
  const qaFields = [
    {
      id: "gGFLfSfVueGGnl4jQ2fu",
      values: [elements["petName"].value],
    },
    {
      id: "mI0g21CUTUZeVMmCpuN9",
      values: [elements["type"].value],
    },
    {
      id: "ESqKjAtIII619wqd4T3R",
      values: [elements["breed"].value],
    },
    {
      id: "ffECYvrdwdsH9pwNmgPt",
      values: [elements["weight"].value],
    },
    {
      id: "QWC1Lqqv7srgXYdDDIq4",
      values: [elements["pets"].value],
    },
    {
      id: "IZOWKVNEWQ5axRdl6Abm",
      values: [
        `https://t1ratmm501.ssc.tsc:8443/groomer-portal/#/CustomerInformation/?partnerId=${crmId}&petId=${elements["pets"].value}`,
      ],
    },
  ];

  const prodFields = [
    {
      id: "cbZK6ATsnmIcuvBs78dh",
      values: [elements["petName"].value],
    },
    {
      id: "Tr154gcceuhFw5bRBYTi",
      values: [elements["type"].value],
    },
    {
      id: "8RZdcFx7BIkRitAn7WUO",
      values: [elements["breed"].value],
    },
    {
      id: "BxSOrKNHa6nSqGLpfrYf",
      values: [elements["weight"].value],
    },
    {
      id: "OJ5kNYIacLSld8FLQuB3",
      values: [elements["pets"].value],
    },
    {
      id: "iDF5QzC7lzZ21pJpXevB",
      values: [
        `https://t1ratmm701.ssc.tsc:8443/groomer-portal/#/CustomerInformation/?partnerId=${crmId}&petId=${elements["pets"].value}`,
      ],
    },
  ];
  waitwhileJSON.dataFields =
    Shopify.shop === "wowteam1.myshopify.com" ? prodFields : qaFields;
  return waitwhileJSON;
}

// Get the current JWT token stored in localstorage. (JWT token expires every 10 minutes)
// We are using localstorage as the API itself cors policy is strict and will only allow request from petsense.com, so even if the jwt token is stolen away the request must come from shop store.
async function getJWTToken(api) {
  // Set the expiry time for the token to 10 minutes
  const expiryTime = 600000;
  const local = JSON.parse(window.localStorage.getItem("tsc-token"));
  const currentTime = Date.now();

  // Check if local is null or the token has expired
  if (!local || currentTime - local.timestamp >= expiryTime) {
    const newToken = await api.getToken();
    window.localStorage.setItem(
      "tsc-token",
      JSON.stringify({ token: newToken, timestamp: currentTime })
    );
    return newToken;
  } else {
    return local.token;
  }
}

// Populate a passed select with given options, map object is to map the value and display based on the option properties.
function populateSelect(options, select, map) {
  options.forEach((optionData) => {
    let option = document.createElement("option");
    option.value = optionData[map["value"]];
    option.innerHTML = optionData[map["display"]];
    select.append(option);
  });
}

// Handler for pet select, can either clear the form for new pets or prefill the form for existing pets.
async function handlePetSelect(petId, api, token, form) {
  if (petId !== "") {
    // In the future this could be stored in a session storage instead of requesting everytime, but due to uncertainty if this is the only place the data can be edited it only does a request for now
    const profile = await api.getPetProfileDetails(token, { ProfileId: petId });
    if (profile) {
      mapProfileToForm(profile, form.elements);
    }
  } else {
    // Clear form and replace inner HTML of button to be create.
    clearForm(form.elements);
  }
  toggleWaitWhileButton();
}

async function createEditProfiles(api, token, inputs, customerId) {
  const profile = mapFormToProfile(inputs);
  const petSelect = inputs["pets"];
  if (profile.ProfileId !== "") {
    // Edit the pet profile and edit the pet select to match new name if it has changed
    api.editPetProfile(token, customerId, profile);
    const petOptions = petSelect.options;
    if (profile.Name !== petOptions[petSelect.selectedIndex].innerHTML) {
      for (let i = 0; i < petOptions.length; i++) {
        if (profile.ProfileId === petOptions[i].value) {
          petOptions[i].innerHTML = profile.Name;
        }
      }
    }
  } else {
    // Add a new pet profile and add it to pet select
    const newId = await api.addPetProfile(token, customerId, profile);
    const newOption = document.createElement("option");
    newOption.value = newId;
    newOption.innerHTML = profile.Name;
    petSelect.append(newOption);
    petSelect.value = newId;
  }
}

function toggleWaitWhileButton(disable) {
  const formButton = document.querySelector("#waitwhileLink");
  if (disable) {
    formButton.setAttribute("disabled", disable);
  } else {
    formButton.removeAttribute("disabled", true);
  }
}

function showIframe(waitWhileLink) {
  const wrapper = document.querySelector(".waitwhile-iframe-wrapper");
  wrapper.classList.add("show");

  const iframe = document.querySelector(".waitwhile-iframe");
  iframe.src = waitWhileLink;

  const yOffset = -100;
  const y = iframe.getBoundingClientRect().top + window.pageYOffset + yOffset;
  window.scrollTo({ top: y, behavior: "smooth" });
  // iframe.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function removeIframe() {
  const wrapper = document.querySelector(".waitwhile-iframe-wrapper");
  if (wrapper.classList.contains("show")) {
    wrapper.classList.remove("show");
    const iframe = document.querySelector(".waitwhile-iframe");
    iframe.src = "";
    toggleWaitWhileButton();
  }
}

function openWaitwhile(elements, crmId) {
  let baseUrl =
    document.getElementById("waitwhile-settings").dataset.waitwhileUrl;
  const storeId = window.sessionStorage.getItem("storeId");

  if (baseUrl.includes("qa") && storeId.includes("qa")) {
    baseUrl = baseUrl.replace("qa", "");
  }

  let waitWhileUrl = baseUrl
    ? `${baseUrl}${storeId}`
    : `https://waitwhile.com/book/petsense${storeId}`;

  /**
   * Edge case specifically testing on dev, when waitwhileURL is set to https://waitwhile.com/book/petsenseqa
   * For DEV, qa is appended to the storeid which causes an issue
   */

  // Waitwhile requires us to rot47 then encode to base64 for their data to be sent.
  let package = mapWaitwhileJSON(elements, crmId);
  // Separate them for easier debugging. The order we are doing is stringify -> rot 47 -> base 64 -> encodeURI
  package = JSON.stringify(package);
  package = rot47(package);
  package = btoa(package);
  package = encodeURIComponent(package);

  // Display waitwhile in an iframe + disable form submission
  toggleWaitWhileButton(true);
  showIframe(`${waitWhileUrl}?data=${package}`);
}

async function handleFormSubmit(event, api, token, customerId, crmId) {
  event.preventDefault();
  await createEditProfiles(api, token, event.target.elements, customerId);
  openWaitwhile(event.target.elements, crmId);
}

// Get the store id from session storage or the url if not found in session storage
function getStoreId() {
  const storeId = new URL(window.location.href).searchParams.get("store");
  const sessionId = window.sessionStorage.getItem("storeId");
  if (!sessionId && storeId) {
    window.sessionStorage.setItem("storeId", storeId);
    return storeId;
  } else if (sessionId) {
    return sessionId;
  } else {
    return null;
  }
}

// Redirect if no storeid or customer id don't exist.
function handleRedirect(storeId, customerId) {
  this.isMobile = window.matchMedia(`(max-width: 991px)`).matches;
  if (!storeId) {
    window.location.replace("/pages/store-locator");
  } else if (!customerId) {
    if (this.isMobile) {
      window.location.replace("/account/login#grooming-login");
    } else {
      window.location.replace("/account/login");
    }
  }
}

async function getCrmId(token, api, customerId, elements) {
  const customer = await api.getCustomer(token, customerId);
  if (customer?.MSGCODE === "200") {
    return customer.PARTNER;
  } else {
    return await api.createCustomer(
      token,
      mapShopifyCustomerToCrmCustomer(elements)
    );
  }
}

// Set each dateForm max date to be today.
function setMaxDate(dateForms) {
  dateForms.forEach(
    (dateForm) => (dateForm.max = new Date().toISOString().split("T")[0])
  );
}

async function initIntegration(customerId) {
  const crmUrl = document.getElementById("waitwhile-settings").dataset.crmUrl;
  const defaultUrl = "https://qa-api.tractorsupply.com";
  const api = crmUrl
    ? new PetsenseCrmApi(crmUrl)
    : new PetsenseCrmApi(defaultUrl);
  const form = document.getElementById("waitwhile-form");
  const cancelWaitWhile = document.querySelectorAll(".waitwhile-cancel");

  if (form) {
    const token = await getJWTToken(api);
    const formInputs = form.elements;
    setMaxDate([formInputs["adoptionDate"], formInputs["birthDate"]]);
    // Attempt to fetch the current crm user with a shop if, if not found create a new crm user.
    const crmId = await getCrmId(token, api, customerId, formInputs);
    const petSelect = formInputs["pets"];
    const breeds = api.getPetBreeds(token);
    const userPets = await api.getPetProfileList(token, {
      shopifyCustomerId: customerId,
    });

    // Resolve both select populations together so they can fire asynchronous from each other.
    Promise.all([breeds, userPets]).then((values) => {
      populateSelect(values[0], formInputs["breed"], {
        value: "BreedName",
        display: "BreedName",
      });
      if (values[1]) {
        populateSelect(values[1], petSelect, {
          value: "ProfileId",
          display: "Name",
        });

        // Change select index.
        const petDropdownSelect = document.querySelector("#selected_pet");
        petDropdownSelect.selectedIndex = 1;

        // Populate pet fields
        const petId = values[1][0].ProfileId;
        handlePetSelect(petId, api, token, form);
      } else {
        toggleWaitWhileButton();
      }
    });

    petSelect.addEventListener("change", async (event) =>
      handlePetSelect(event.target.value, api, token, form)
    );
    form.addEventListener("submit", async (event) =>
      handleFormSubmit(event, api, token, customerId, crmId)
    );

    cancelWaitWhile.forEach((button) => {
      button.addEventListener("click", () => {
        removeIframe();
      });
    });
  }
}

window.addEventListener("DOMContentLoaded", () => {
  const customerId =
    document.getElementById("waitwhile-form").elements["id"].value;
  const storeId = getStoreId();
  handleRedirect(storeId, customerId);
  initIntegration(customerId);
});
